import { SdkContext } from '../internal'

/** F-164: where a scheduled post stands (`failed`: the author could no longer post in the space). */
export type PostScheduleStatus = 'scheduled' | 'published' | 'failed'

/** `schedule` on a content or status answer; `null` on a post never scheduled. */
export interface PostSchedule {
  status: PostScheduleStatus
  publishAt: string
  failure: string | null
}

/** A row of "Agendados" (`GET /api/scheduled-posts`). */
export interface ScheduledPost {
  id: string
  type: 'content' | 'status'
  title: string | null
  excerpt: string | null
  slug: string | null
  space: { id: string; title: string; slug: string }
  author: { id: string; name: string; avatar?: string | null }
  publishAt: string
  status: 'scheduled' | 'failed'
  /** `authorNoAccess` when the author can no longer post in the space. */
  failure: string | null
  canManage: boolean
}

export interface ScheduledPostsPage {
  items: ScheduledPost[]
  pagination?: { page: number; limit: number; total: number; totalPages: number; hasNext?: boolean }
}

/**
 * F-164 "Agendados": the team (owner, admin, moderator) sees every waiting or
 * refused post; anyone else only their own. Rescheduling is `PUT` of the post
 * with `publishAt`; canceling is deleting the post.
 */
export class ScheduledPostsResource {
  constructor(private ctx: SdkContext) {}

  async list(params: { spaceId?: string; page?: number; limit?: number } = {}): Promise<ScheduledPostsPage> {
    const r = await this.ctx.raw({ httpMethod: 'GET', path: '/scheduled-posts' }, { query: params }) as unknown
    if (Array.isArray(r)) return { items: r as ScheduledPost[] }
    const page = r as { items?: ScheduledPost[]; data?: ScheduledPost[]; pagination?: ScheduledPostsPage['pagination'] }
    return { items: page.items ?? page.data ?? [], pagination: page.pagination }
  }

  /** "Publicar agora": the date becomes now; a refused one is tried again (409 when the author still has no access). */
  async publishNow(type: 'content' | 'status', id: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'POST', path: '/scheduled-posts/{type}/{id}/publish', hasBody: true }, { type, id, body: {} })
  }
}
